const {reportToClarity} = require('./clarityReporter');

const STORAGE_KEY = 'kd.analytics-diagnostics.v1';
const DEBUG_STORAGE_KEY = 'kd.analytics-debug';
const MAX_ENTRIES = 100;

const FAILURE_STAGES = new Set([
    'data_layer_error',
    'gtm_load_error',
    'queue_corrupt',
    'queue_expired',
    'queue_flush_error',
    'storage_fallback',
]);

let entries = [];

function safeSessionStorage() {
    try {
        return window.sessionStorage;
    } catch (error) {
        return null;
    }
}

function readStoredEntries() {
    const storage = safeSessionStorage();

    if (!storage) {
        return [];
    }

    try {
        const storedEntries = JSON.parse(storage.getItem(STORAGE_KEY));
        return Array.isArray(storedEntries) ? storedEntries.slice(-MAX_ENTRIES) : [];
    } catch (error) {
        return [];
    }
}

function persistEntries() {
    const storage = safeSessionStorage();

    if (!storage) {
        return;
    }

    try {
        storage.setItem(STORAGE_KEY, JSON.stringify(entries));
    } catch (error) {
        // Diagnostics must never interfere with analytics or navigation.
    }
}

function normalizeLabel(value) {
    if (typeof value !== 'string' || value.length === 0) {
        return null;
    }

    return value.replace(/[^a-zA-Z0-9_.-]/g, '_').slice(0, 100);
}

function getCurrentPageType() {
    if (typeof window.kdAnalytics === 'object' && window.kdAnalytics.pageType) {
        return normalizeLabel(window.kdAnalytics.pageType);
    }

    return 'unclassified';
}

function createEventId() {
    if (window.crypto && typeof window.crypto.randomUUID === 'function') {
        return window.crypto.randomUUID();
    }

    return Date.now().toString(36) + '-' + Math.random().toString(36).slice(2, 10);
}

function isDebugEnabled() {
    const storage = safeSessionStorage();

    try {
        const queryValue = new URLSearchParams(window.location.search).get('kd_analytics_debug');

        if (queryValue === '1') {
            storage?.setItem(DEBUG_STORAGE_KEY, '1');
            return true;
        }

        if (queryValue === '0') {
            storage?.removeItem(DEBUG_STORAGE_KEY);
            return false;
        }
    } catch (error) {
        // Fall through to the persisted preference.
    }

    try {
        return storage?.getItem(DEBUG_STORAGE_KEY) === '1';
    } catch (error) {
        return false;
    }
}

function recordDiagnostic(stage, context = {}) {
    const entry = {
        eventId: normalizeLabel(context.eventId),
        eventName: normalizeLabel(context.eventName),
        page: getCurrentPageType(),
        source: normalizeLabel(context.source),
        stage: normalizeLabel(stage),
        targetPage: normalizeLabel(context.targetPage),
        timestamp: Date.now(),
    };

    entries.push(entry);
    entries = entries.slice(-MAX_ENTRIES);
    persistEntries();
    reportToClarity(
        entry,
        FAILURE_STAGES.has(entry.stage),
        entries.some(function (diagnosticEntry) {
            return FAILURE_STAGES.has(diagnosticEntry.stage);
        })
    );

    if (isDebugEnabled() && window.console && typeof window.console.info === 'function') {
        window.console.info('[KD Analytics]', entry);
    }

    return entry;
}

function getEntries() {
    return entries.map(function (entry) {
        return Object.assign({}, entry);
    });
}

function getSummary() {
    const events = new Map();
    let gtmStatus = 'unknown';
    let observedGaRequests = 0;
    let observedMetaRequests = 0;
    let requestObserverStatus = 'unknown';

    entries.forEach(function (entry) {
        if (entry.stage === 'gtm_loaded' || entry.stage === 'gtm_load_error') {
            gtmStatus = entry.stage;
        }

        if (entry.stage === 'ga_request_observed') {
            observedGaRequests += 1;
        }

        if (entry.stage === 'meta_request_observed') {
            observedMetaRequests += 1;
        }

        if (entry.stage === 'resource_observer_started' || entry.stage === 'resource_observer_unavailable') {
            requestObserverStatus = entry.stage;
        }

        if (!entry.eventId) {
            return;
        }

        if (!events.has(entry.eventId)) {
            events.set(entry.eventId, {
                eventId: entry.eventId,
                eventName: entry.eventName,
                stages: [],
            });
        }

        events.get(entry.eventId).stages.push(entry.stage);
    });

    const eventSummaries = Array.from(events.values()).map(function (event) {
        return Object.assign({}, event, {
            status: event.stages[event.stages.length - 1],
        });
    });
    const failures = entries.filter(function (entry) {
        return FAILURE_STAGES.has(entry.stage);
    }).length;
    const pendingEvents = eventSummaries.filter(function (event) {
        return ['queued', 'queue_flushed', 'data_layer_push'].includes(event.status);
    }).length;

    return {
        debugEnabled: isDebugEnabled(),
        events: eventSummaries,
        failures,
        gtmStatus,
        health: failures > 0 ? 'attention' : (gtmStatus === 'gtm_loaded' ? 'ok' : 'pending'),
        observedGaRequests,
        observedMetaRequests,
        pendingEvents,
        requestObserverStatus,
    };
}

function clearDiagnostics() {
    entries = [];
    const storage = safeSessionStorage();

    try {
        storage?.removeItem(STORAGE_KEY);
    } catch (error) {
        // The in-memory log has still been cleared.
    }
}

function setDebugEnabled(enabled) {
    const storage = safeSessionStorage();

    if (!storage) {
        return false;
    }

    try {
        if (enabled) {
            storage.setItem(DEBUG_STORAGE_KEY, '1');
        } else {
            storage.removeItem(DEBUG_STORAGE_KEY);
        }
    } catch (error) {
        return false;
    }

    return true;
}

function printDiagnostics() {
    const summary = getSummary();

    if (!window.console) {
        return summary;
    }

    if (typeof window.console.group === 'function') {
        window.console.group('[KD Analytics] diagnostics');
    }
    if (typeof window.console.log === 'function') {
        window.console.log(summary);
    }
    if (typeof window.console.table === 'function') {
        window.console.table(getEntries());
    }
    if (typeof window.console.groupEnd === 'function') {
        window.console.groupEnd();
    }

    return summary;
}

function installPublicApi() {
    if (window.kdAnalyticsDiagnostics) {
        return;
    }

    entries = readStoredEntries();

    window.kdAnalyticsDiagnostics = Object.freeze({
        clear: clearDiagnostics,
        disableDebug: function () {
            return setDebugEnabled(false);
        },
        enableDebug: function () {
            return setDebugEnabled(true);
        },
        entries: getEntries,
        print: printDiagnostics,
        summary: getSummary,
        version: '1.0.0',
    });

    if (isDebugEnabled() && window.console && typeof window.console.info === 'function') {
        window.console.info(
            '[KD Analytics] Debug mode enabled. Run kdAnalyticsDiagnostics.print() for the full report.'
        );
    }
}

module.exports = {
    clearDiagnostics,
    createEventId,
    getEntries,
    getSummary,
    installPublicApi,
    recordDiagnostic,
};
